<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTenant } from '@/composables/useTenant';
import { INITIAL_TEAMS, INITIAL_PLAYERS } from '@/services/mockData';
import type { PlayerProfile } from '@/types/models';
import AppBadge from '@/components/ui/AppBadge.vue';
import AppModal from '@/components/ui/AppModal.vue';
import { useToastNotification } from '@/composables/useToastNotification';
import { NButton, NProgress, NTag } from 'naive-ui';
import {
  Trophy,
  Activity,
  Flame,
  RotateCcw,
  ShieldAlert,
  Clock,
  ArrowRight,
  Sparkles,
  ChevronRight,
  Crosshair,
  UserCheck,
  RefreshCw
} from 'lucide-vue-next';

const { t } = useI18n();
const { activeTenant } = useTenant();
const { notifySuccess, notifyInfo, notifyError } = useToastNotification();

// Active Game State
const homeTeam = computed(() => INITIAL_TEAMS[0]);
const awayTeamName = ref('Bay Area Bombers');

const inning = ref(3);
const isTop = ref(false); // false = Bot 3rd
const homeScore = ref(4);
const awayScore = ref(2);

const balls = ref(1);
const strikes = ref(2);
const outs = ref(1);

// Line Score (Innings 1-9)
const lineScore = ref<{ inning: number; away: number | string; home: number | string }[]>([
  { inning: 1, away: 1, home: 0 },
  { inning: 2, away: 0, home: 2 },
  { inning: 3, away: 1, home: 2 },
  { inning: 4, away: '-', home: '-' },
  { inning: 5, away: '-', home: '-' },
  { inning: 6, away: '-', home: '-' },
  { inning: 7, away: '-', home: '-' },
  { inning: 8, away: '-', home: '-' },
  { inning: 9, away: '-', home: '-' },
]);

// Hits & Errors
const awayHits = ref(5);
const awayErrors = ref(1);
const homeHits = ref(7);
const homeErrors = ref(0);

// Runners on base: 1st, 2nd, 3rd
const runners = ref({
  first: true,
  second: false,
  third: true,
});

// Pitch Smart Pitcher Tracking & Bullpen
const availablePitchers = computed(() =>
  INITIAL_PLAYERS.filter(p => p.position === 'P' || p.secondaryPosition === 'P')
);

const currentPitcher = ref<PlayerProfile>(INITIAL_PLAYERS[1]); // Lucas Ortiz #11
const isPitcherModalOpen = ref(false);
const pitchCount = ref(68);
const pitchHistory = ref<Array<{ x: number; y: number; type: 'ball' | 'strike' | 'hit'; velo: number }>>([
  { x: 45, y: 55, type: 'strike', velo: 92 },
  { x: 30, y: 40, type: 'ball', velo: 91 },
  { x: 60, y: 65, type: 'strike', velo: 84 },
  { x: 50, y: 35, type: 'hit', velo: 93 },
  { x: 75, y: 25, type: 'ball', velo: 89 },
]);

const maxPitchLimit = computed(() => {
  if (activeTenant.value.type === 'ACADEMIC') return 95;
  if (activeTenant.value.type === 'AMATEUR') return 105;
  if (activeTenant.value.type === 'PRO') return 115;
  return 100; // Others
});

const restDaysRequired = computed(() => {
  const c = pitchCount.value;
  if (c >= 76) return 4;
  if (c >= 51) return 3;
  if (c >= 36) return 2;
  if (c >= 21) return 1;
  return 0;
});

const pitchLimitPct = computed(() => {
  if (!maxPitchLimit.value || maxPitchLimit.value <= 0) return 0;
  return Math.min(100, Math.round((pitchCount.value / maxPitchLimit.value) * 100));
});

interface PlayLogItem {
  id: string;
  time: string;
  inningText: string;
  description: string;
  type: 'pitch' | 'hit' | 'out' | 'run';
}

const playLog = ref<PlayLogItem[]>([
  {
    id: '1',
    time: '14:22',
    inningText: 'Bot 3',
    description: '山田 雄大 (#24) が左中間を深々と破る適時二塁打！一塁走者が生還！',
    type: 'hit',
  },
  {
    id: '2',
    time: '14:20',
    inningText: 'Bot 3',
    description: '中村 拓海 (#7) がフルカウントから四球を選び出塁。',
    type: 'pitch',
  },
  {
    id: '3',
    time: '14:15',
    inningText: 'Top 3',
    description: '佐藤 翔太 (#18) が内角152km/hストレートで見逃し三振を奪う！',
    type: 'out',
  },
]);

const logPlay = (desc: string, type: PlayLogItem['type'] = 'pitch') => {
  const item: PlayLogItem = {
    id: String(Date.now()),
    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    inningText: isTop.value ? `Top ${inning.value}` : `Bot ${inning.value}`,
    description: desc,
    type,
  };
  playLog.value.unshift(item);
};

const handlePitch = (result: 'ball' | 'strike' | 'foul') => {
  pitchCount.value += 1;
  
  // Randomize strike zone pitch location dot
  const randomX = Math.floor(Math.random() * 70) + 15;
  const randomY = Math.floor(Math.random() * 70) + 15;
  pitchHistory.value.push({
    x: randomX,
    y: randomY,
    type: result === 'ball' ? 'ball' : 'strike',
    velo: Math.floor(Math.random() * 8) + 88,
  });

  if (pitchCount.value >= maxPitchLimit.value) {
    notifyError(t('liveGame.pitchLimitWarning'), t('liveGame.pitchLimitWarningMsg', {
      pitcher: currentPitcher.value.lastName,
      count: pitchCount.value,
      days: restDaysRequired.value
    }));
  }

  if (result === 'ball') {
    if (balls.value < 3) {
      balls.value += 1;
      logPlay(`Ball called (Count ${balls.value}-${strikes.value})`, 'pitch');
    } else {
      balls.value = 0;
      strikes.value = 0;
      runners.value.first = true;
      logPlay(`Walk (BB) on 4 balls. Runner advances.`, 'pitch');
      notifyInfo('Walk (BB)', 'Batter awarded first base.');
    }
  } else if (result === 'strike') {
    if (strikes.value < 2) {
      strikes.value += 1;
      logPlay(`Strike swinging (Count ${balls.value}-${strikes.value})`, 'pitch');
    } else {
      balls.value = 0;
      strikes.value = 0;
      handleOut('Strikeout swinging (K)');
    }
  } else if (result === 'foul') {
    if (strikes.value < 2) strikes.value += 1;
    logPlay(`Foul ball (Count ${balls.value}-${strikes.value})`, 'pitch');
  }
};

const handleHit = (hitType: '1B' | '2B' | '3B' | 'HR') => {
  pitchCount.value += 1;
  balls.value = 0;
  strikes.value = 0;

  if (isTop.value) awayHits.value += 1;
  else homeHits.value += 1;

  if (hitType === '1B') {
    runners.value.first = true;
    logPlay(`Single (1B) sharply into right field.`, 'hit');
  } else if (hitType === '2B') {
    runners.value.second = true;
    runners.value.first = false;
    logPlay(`Double (2B) driven into right-center gap!`, 'hit');
  } else if (hitType === '3B') {
    runners.value.third = true;
    runners.value.first = false;
    runners.value.second = false;
    logPlay(`Triple (3B) down the right field line!`, 'hit');
  } else if (hitType === 'HR') {
    homeScore.value += 1;
    runners.value = { first: false, second: false, third: false };
    logPlay(`HOME RUN (HR)! Clears the centerfield fence!`, 'hit');
    notifySuccess('HOME RUN!', 'Runners clear the bases.');
  }
};

const handleOut = (reason = 'Out recorded') => {
  balls.value = 0;
  strikes.value = 0;

  if (outs.value < 2) {
    outs.value += 1;
    logPlay(`${reason} (Out #${outs.value})`, 'out');
  } else {
    advanceInning();
  }
};

const handleRunScored = () => {
  if (isTop.value) {
    awayScore.value += 1;
  } else {
    homeScore.value += 1;
  }
  logPlay(`Run scored! Current score: ${homeTeam.value.name} ${homeScore.value} - ${awayTeamName.value} ${awayScore.value}`, 'run');
  notifySuccess(t('liveGame.runScored'), `${homeScore.value} - ${awayScore.value}`);
};

const advanceInning = () => {
  outs.value = 0;
  balls.value = 0;
  strikes.value = 0;
  runners.value = { first: false, second: false, third: false };

  if (isTop.value) {
    isTop.value = false;
  } else {
    isTop.value = true;
    inning.value += 1;
  }

  logPlay(`Inning change: ${isTop.value ? `Top ${inning.value}` : `Bot ${inning.value}`}`, 'out');
  notifyInfo(t('liveGame.advanceInning'), isTop.value ? `Top ${inning.value}` : `Bot ${inning.value}`);
};

const resetCount = () => {
  balls.value = 0;
  strikes.value = 0;
};

const changePitcher = (p: PlayerProfile) => {
  currentPitcher.value = p;
  pitchCount.value = 0;
  pitchHistory.value = [];
  isPitcherModalOpen.value = false;
  notifySuccess('Pitcher Substitution', `#${p.jerseyNumber} ${p.firstName} ${p.lastName} enters the game.`);
};
</script>

<template>
  <div class="space-y-6 animate-in fade-in duration-200">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-800/80">
      <div>
        <div class="flex items-center gap-2">
          <h1 class="text-2xl sm:text-3xl font-extrabold text-slate-100 tracking-tight">
            {{ t('liveGame.title') }}
          </h1>
          <AppBadge variant="accent">Pitch Smart Active</AppBadge>
        </div>
        <p class="mt-1 text-xs sm:text-sm text-slate-400">
          {{ t('liveGame.subtitle', { name: activeTenant.name }) }}
        </p>
      </div>

      <div class="flex items-center gap-2">
        <NButton
          secondary
          @click="isPitcherModalOpen = true"
          class="font-semibold text-xs"
        >
          <template #icon><RefreshCw class="w-3.5 h-3.5 text-sky-400" /></template>
          Sub Pitcher
        </NButton>
        <NButton
          type="primary"
          @click="advanceInning"
          class="font-bold text-xs shadow-lg shadow-sky-500/20"
        >
          {{ t('liveGame.advanceInning') }}
        </NButton>
      </div>
    </div>

    <!-- Official Inning Box Score Table -->
    <div class="bg-slate-900 border border-slate-800 rounded-2xl p-4 shadow-xl overflow-x-auto">
      <table class="w-full text-xs text-center border-collapse">
        <thead>
          <tr class="border-b border-slate-800 text-[10px] uppercase font-bold text-slate-400">
            <th class="py-2 px-3 text-left w-36">Team</th>
            <th v-for="n in 9" :key="n" :class="['py-2 px-2.5', inning === n ? 'text-sky-400 font-extrabold bg-sky-500/10 rounded-t' : '']">{{ n }}</th>
            <th class="py-2 px-3 font-bold text-slate-200 border-l border-slate-800">R</th>
            <th class="py-2 px-3 font-bold text-slate-200">H</th>
            <th class="py-2 px-3 font-bold text-slate-200">E</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-800/80 font-mono">
          <!-- Away Row -->
          <tr class="hover:bg-slate-950/40">
            <td class="py-2 px-3 text-left font-sans font-bold text-slate-300 truncate max-w-[140px]">{{ awayTeamName }}</td>
            <td v-for="item in lineScore" :key="item.inning" :class="['py-2 px-2.5', inning === item.inning && isTop ? 'text-amber-400 font-bold bg-sky-500/10' : 'text-slate-400']">
              {{ item.away }}
            </td>
            <td class="py-2 px-3 font-extrabold text-slate-100 border-l border-slate-800">{{ awayScore }}</td>
            <td class="py-2 px-3 text-slate-300">{{ awayHits }}</td>
            <td class="py-2 px-3 text-slate-400">{{ awayErrors }}</td>
          </tr>
          <!-- Home Row -->
          <tr class="hover:bg-slate-950/40">
            <td class="py-2 px-3 text-left font-sans font-bold text-sky-300 truncate max-w-[140px]">{{ homeTeam.name }}</td>
            <td v-for="item in lineScore" :key="item.inning" :class="['py-2 px-2.5', inning === item.inning && !isTop ? 'text-amber-400 font-bold bg-sky-500/10' : 'text-slate-400']">
              {{ item.home }}
            </td>
            <td class="py-2 px-3 font-extrabold text-sky-400 border-l border-slate-800">{{ homeScore }}</td>
            <td class="py-2 px-3 text-slate-300">{{ homeHits }}</td>
            <td class="py-2 px-3 text-slate-400">{{ homeErrors }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Live Inning Scoreboard & Pitch Smart Banner -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <!-- Main Scoreboard (7 Cols) -->
      <div class="lg:col-span-7 bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-6">
        <!-- Inning & BSO Indicator & Diamond Runners -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
          <!-- Inning pill -->
          <div class="p-3 bg-slate-950 rounded-2xl border border-slate-800 text-center">
            <span class="text-[10px] uppercase font-bold text-slate-400 block">{{ t('liveGame.inning', { inning }) }}</span>
            <span class="text-lg font-black text-amber-400 mt-0.5 block">
              {{ isTop ? '▲ Top' : '▼ Bot' }} {{ inning }}
            </span>
          </div>

          <!-- B / S / O Lights -->
          <div class="p-3 bg-slate-950 rounded-2xl border border-slate-800 space-y-2">
            <!-- Balls -->
            <div class="flex items-center justify-between text-xs font-mono">
              <span class="text-slate-400 font-bold">B:</span>
              <div class="flex gap-1.5">
                <span v-for="b in 3" :key="b" :class="['w-3 h-3 rounded-full transition-colors', b <= balls ? 'bg-emerald-400 shadow-sm shadow-emerald-400/50' : 'bg-slate-800']" />
              </div>
            </div>
            <!-- Strikes -->
            <div class="flex items-center justify-between text-xs font-mono">
              <span class="text-slate-400 font-bold">S:</span>
              <div class="flex gap-1.5">
                <span v-for="s in 2" :key="s" :class="['w-3 h-3 rounded-full transition-colors', s <= strikes ? 'bg-rose-500 shadow-sm shadow-rose-500/50' : 'bg-slate-800']" />
              </div>
            </div>
            <!-- Outs -->
            <div class="flex items-center justify-between text-xs font-mono">
              <span class="text-slate-400 font-bold">O:</span>
              <div class="flex gap-1.5">
                <span v-for="o in 2" :key="o" :class="['w-3 h-3 rounded-full transition-colors', o <= outs ? 'bg-amber-400 shadow-sm shadow-amber-400/50' : 'bg-slate-800']" />
              </div>
            </div>
          </div>

          <!-- Base Runner Diamond Mini -->
          <div class="p-3 bg-slate-950 rounded-2xl border border-slate-800 flex flex-col items-center justify-center relative min-h-[90px]">
            <span class="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1">{{ t('liveGame.runnersOnBase') }}</span>
            <div class="relative w-12 h-12">
              <!-- 2nd base -->
              <div :class="['absolute top-0 left-1/2 -translate-x-1/2 w-3.5 h-3.5 rotate-45 border transition-colors', runners.second ? 'bg-amber-400 border-amber-300 shadow-md shadow-amber-400/50' : 'bg-slate-800 border-slate-700']" />
              <!-- 3rd base -->
              <div :class="['absolute top-1/2 left-0 -translate-y-1/2 w-3.5 h-3.5 rotate-45 border transition-colors', runners.third ? 'bg-amber-400 border-amber-300 shadow-md shadow-amber-400/50' : 'bg-slate-800 border-slate-700']" />
              <!-- 1st base -->
              <div :class="['absolute top-1/2 right-0 -translate-y-1/2 w-3.5 h-3.5 rotate-45 border transition-colors', runners.first ? 'bg-amber-400 border-amber-300 shadow-md shadow-amber-400/50' : 'bg-slate-800 border-slate-700']" />
              <!-- Home plate -->
              <div class="absolute bottom-0 left-1/2 -translate-x-1/2 w-3 h-3 bg-slate-600 rotate-45" />
            </div>
          </div>
        </div>

        <!-- Quick Play Action Triggers -->
        <div class="space-y-3 pt-2">
          <div class="flex items-center justify-between">
            <span class="text-xs font-bold uppercase tracking-wider text-slate-400">{{ t('liveGame.quickPlays') }}</span>
            <button type="button" @click="resetCount" class="text-[11px] font-semibold text-slate-400 hover:text-slate-200">
              {{ t('liveGame.resetCount') }}
            </button>
          </div>

          <!-- Pitch Buttons -->
          <div class="grid grid-cols-3 gap-2">
            <button
              type="button"
              @click="handlePitch('ball')"
              class="py-2.5 px-3 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 font-bold text-xs rounded-xl transition"
            >
              {{ t('liveGame.ball') }}
            </button>
            <button
              type="button"
              @click="handlePitch('strike')"
              class="py-2.5 px-3 bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/30 text-rose-300 font-bold text-xs rounded-xl transition"
            >
              {{ t('liveGame.strike') }}
            </button>
            <button
              type="button"
              @click="handlePitch('foul')"
              class="py-2.5 px-3 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-300 font-bold text-xs rounded-xl transition"
            >
              {{ t('liveGame.foul') }}
            </button>
          </div>

          <!-- Hit / Play Buttons -->
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <button
              type="button"
              @click="handleHit('1B')"
              class="py-2 px-3 bg-slate-950 hover:bg-slate-800 border border-slate-800 font-bold text-xs text-sky-400 rounded-xl transition"
            >
              {{ t('liveGame.hitSingle') }}
            </button>
            <button
              type="button"
              @click="handleHit('2B')"
              class="py-2 px-3 bg-slate-950 hover:bg-slate-800 border border-slate-800 font-bold text-xs text-sky-400 rounded-xl transition"
            >
              {{ t('liveGame.hitDouble') }}
            </button>
            <button
              type="button"
              @click="handleHit('3B')"
              class="py-2 px-3 bg-slate-950 hover:bg-slate-800 border border-slate-800 font-bold text-xs text-sky-400 rounded-xl transition"
            >
              {{ t('liveGame.hitTriple') }}
            </button>
            <button
              type="button"
              @click="handleHit('HR')"
              class="py-2 px-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs rounded-xl transition shadow-md shadow-amber-500/20"
            >
              {{ t('liveGame.homeRun') }}
            </button>
          </div>

          <!-- Out & Run Score Buttons -->
          <div class="grid grid-cols-2 gap-2 pt-1">
            <button
              type="button"
              @click="handleOut('Batted ball out')"
              class="py-2.5 px-3 bg-slate-950 hover:bg-slate-800 border border-slate-800 font-bold text-xs text-slate-300 rounded-xl transition"
            >
              {{ t('liveGame.outInPlay') }}
            </button>
            <button
              type="button"
              @click="handleRunScored"
              class="py-2.5 px-3 bg-sky-500 hover:bg-sky-400 font-extrabold text-xs text-slate-950 rounded-xl transition shadow-md shadow-sky-500/20"
            >
              {{ t('liveGame.runScored') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Pitch Smart Compliance & Strike Zone Locator (5 Cols) -->
      <div class="lg:col-span-5 space-y-4">
        <!-- Pitch Smart Gauge Card -->
        <div class="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <Activity class="w-4 h-4 text-emerald-400" />
              <h3 class="text-xs font-bold uppercase tracking-wider text-slate-200">Pitch Smart USA Safety</h3>
            </div>
            <NTag type="info" size="small" round :bordered="false">{{ activeTenant.type }}</NTag>
          </div>

          <!-- Active Pitcher Info -->
          <div class="p-4 bg-slate-950 rounded-2xl border border-slate-800 flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-sky-500/20 text-sky-400 font-extrabold text-sm flex items-center justify-center">
                #{{ currentPitcher.jerseyNumber }}
              </div>
              <div>
                <p class="text-xs font-bold text-slate-100">{{ currentPitcher.firstName }} {{ currentPitcher.lastName }}</p>
                <p class="text-[11px] text-slate-400">Throws: {{ currentPitcher.throws }} &bull; Class {{ currentPitcher.graduationYear || 2025 }}</p>
              </div>
            </div>
            <div class="text-right">
              <span class="text-2xl font-black font-mono text-emerald-400">{{ pitchCount }}</span>
              <span class="text-[10px] text-slate-500 block uppercase font-bold">Pitches</span>
            </div>
          </div>

          <!-- Progress Bar towards Limit -->
          <div class="space-y-1.5">
            <div class="flex items-center justify-between text-xs">
              <span class="text-slate-400">{{ t('liveGame.pitchLimit', { limit: maxPitchLimit }) }}</span>
              <span class="font-mono font-bold text-slate-200">{{ pitchLimitPct }}%</span>
            </div>
            <NProgress
              type="line"
              :percentage="pitchLimitPct"
              :status="pitchLimitPct >= 90 ? 'error' : pitchLimitPct >= 70 ? 'warning' : 'success'"
              :show-indicator="false"
            />
          </div>

          <!-- Mandatory Rest Days Required Notice -->
          <div class="p-3.5 bg-slate-950 rounded-2xl border border-slate-800 flex items-center justify-between text-xs">
            <span class="text-slate-400">{{ t('liveGame.restDaysRequired', { days: restDaysRequired }) }}</span>
            <span :class="['px-2 py-0.5 rounded font-bold font-mono', restDaysRequired >= 3 ? 'bg-rose-500/20 text-rose-300' : 'bg-emerald-500/20 text-emerald-300']">
              {{ restDaysRequired }} Days Rest
            </span>
          </div>

          <!-- Strike Zone Heat Map Mini -->
          <div class="pt-2 border-t border-slate-800">
            <div class="flex items-center justify-between text-[11px] text-slate-400 font-bold mb-2">
              <span class="flex items-center gap-1"><Crosshair class="w-3.5 h-3.5 text-sky-400" /> Strike Zone Matrix</span>
              <span class="text-slate-500 font-mono">{{ pitchHistory.length }} recorded</span>
            </div>
            <div class="relative w-full h-32 bg-slate-950 rounded-2xl border border-slate-800 flex items-center justify-center overflow-hidden">
              <!-- 9-box strike zone -->
              <div class="w-24 h-24 border-2 border-sky-400/60 grid grid-cols-3 grid-rows-3 relative bg-sky-500/5">
                <div v-for="cell in 9" :key="cell" class="border border-sky-500/20" />
              </div>
              <!-- Pitch dots -->
              <div
                v-for="(p, idx) in pitchHistory"
                :key="idx"
                :class="[
                  'absolute w-2.5 h-2.5 rounded-full border ring-1 transition-all',
                  p.type === 'strike' ? 'bg-rose-500 border-white ring-rose-500/50' : p.type === 'ball' ? 'bg-emerald-400 border-slate-950 ring-emerald-400/50' : 'bg-amber-400 border-white ring-amber-400/50'
                ]"
                :style="{ top: `${p.y}%`, left: `${p.x}%` }"
                :title="`${p.velo} MPH (${p.type})`"
              />
            </div>
          </div>
        </div>

        <!-- Live Play-by-Play Stream -->
        <div class="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-2xl space-y-3">
          <h3 class="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
            <Clock class="w-4 h-4 text-sky-400" />
            {{ t('liveGame.gameLog') }}
          </h3>

          <div class="max-h-52 overflow-y-auto space-y-2 pr-1 text-xs">
            <div
              v-for="log in playLog"
              :key="log.id"
              class="p-2.5 bg-slate-950 rounded-xl border border-slate-800/80 flex items-start gap-2.5"
            >
              <span class="font-mono text-[10px] text-sky-400 font-bold px-1.5 py-0.5 rounded bg-sky-500/10 shrink-0">
                {{ log.inningText }}
              </span>
              <div class="flex-1 min-w-0">
                <p class="text-slate-200 leading-snug">{{ log.description }}</p>
                <span class="text-[10px] text-slate-500 mt-0.5 block">{{ log.time }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bullpen Substitution Modal -->
    <AppModal
      v-model="isPitcherModalOpen"
      title="Bullpen Substitution"
      subtitle="Select a relief pitcher from the active roster"
      max-width="md"
    >
      <div class="space-y-3">
        <div
          v-for="p in availablePitchers"
          :key="p.id"
          @click="changePitcher(p)"
          :class="[
            'p-3.5 rounded-2xl border cursor-pointer transition flex items-center justify-between',
            p.id === currentPitcher.id ? 'bg-sky-500/20 border-sky-500 ring-2 ring-sky-500/30' : 'bg-slate-950 border-slate-800 hover:border-slate-700'
          ]"
        >
          <div class="flex items-center gap-3">
            <span class="w-8 h-8 rounded-xl bg-sky-500/15 text-sky-400 font-mono font-bold text-xs flex items-center justify-center">
              #{{ p.jerseyNumber }}
            </span>
            <div>
              <p class="text-xs font-bold text-slate-200">{{ p.firstName }} {{ p.lastName }}</p>
              <p class="text-[11px] text-slate-400">Throws: {{ p.throws }} &bull; Rested</p>
            </div>
          </div>
          <AppBadge :variant="p.id === currentPitcher.id ? 'primary' : 'success'">
            {{ p.id === currentPitcher.id ? 'On Mound' : 'Available' }}
          </AppBadge>
        </div>

        <div class="pt-3 flex justify-end">
          <NButton quaternary @click="isPitcherModalOpen = false">Cancel</NButton>
        </div>
      </div>
    </AppModal>
  </div>
</template>